import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Save, Loader2, Search, UserCheck, MapPin, BadgeCheck, User, X, CalendarClock, Flame, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DashboardShell, StatCard } from "@/components/DashboardShell";
import { ComingSoon } from "@/components/ComingSoon";
import { CountrySelect, PhoneField } from "@/components/CountryPhone";
import { PhotoUpload } from "@/components/PhotoUpload";
import { FindPartnerDialog } from "@/components/FindPartnerDialog";
import { MemberAccountability } from "@/components/MemberAccountability";
import { MemberBilling } from "@/components/MemberBilling";
import { COUNTRY_BY_CODE, localFromE164 } from "@/data/countries";
import { SPORTS, SPORT_ICONS } from "@/data/sports";
import { AVAILABILITY_OPTIONS, MEMBER_MODES } from "@/data/matching";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function MemberDashboard() {
  const { user, refreshUser } = useAuth();
  const p = user?.profile || {};
  const [photo, setPhoto] = useState(p.photo || "");
  const [bio, setBio] = useState(p.bio || "");
  const [sports, setSports] = useState(p.sports || []);
  const [goal, setGoal] = useState("");
  const [goals, setGoals] = useState(p.goals || []);
  const [country, setCountry] = useState(COUNTRY_BY_CODE[user?.country_code] || null);
  const [phone, setPhone] = useState(localFromE164(user?.phone, user?.dial_code));
  const [state, setState] = useState(p.state || "");
  const [city, setCity] = useState(p.city || "");
  const [availability, setAvailability] = useState(p.preferred_availability || []);
  const [mode, setMode] = useState(p.preferred_mode || "Either");
  const [saving, setSaving] = useState(false);

  const [findOpen, setFindOpen] = useState(false);
  const [current, setCurrent] = useState({ active: null, pending: [] });
  const [ending, setEnding] = useState(false);

  const loadMatches = useCallback(async () => {
    try {
      const { data } = await api.get("/matches/current");
      setCurrent(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadMatches(); }, [loadMatches]);

  const toggleSport = (slug) => setSports((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]));
  const toggleAvail = (a) => setAvailability((s) => (s.includes(a) ? s.filter((x) => x !== a) : [...s, a]));
  const addGoal = () => { const g = goal.trim(); if (g && !goals.includes(g)) { setGoals([...goals, g]); setGoal(""); } };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { bio, sports, goals, state, city, photo, preferred_availability: availability, preferred_mode: mode };
      if (country) {
        payload.country = country.name; payload.country_code = country.code; payload.dial_code = country.dial;
        payload.phone = phone.trim() ? `${country.dial}${phone.replace(/\D/g, "").replace(/^0+/, "")}` : "";
      }
      await api.put("/profile", payload);
      await refreshUser();
      toast.success("Profile saved!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const endMatch = async () => {
    if (!current.active) return;
    setEnding(true);
    try {
      await api.post(`/matches/${current.active.id}/end`);
      toast.success("Relationship ended. You can find a new partner.");
      await loadMatches();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setEnding(false);
    }
  };

  const activePartner = current.active?.partner;
  const pendingIds = (current.pending || []).map((x) => x.partner.id);

  return (
    <DashboardShell testid="member-dashboard">
      <div className="mb-8" data-testid="member-dashboard-header">
        <span className="text-xs font-bold uppercase tracking-widest text-[#E53E3E]">Member Dashboard</span>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-black uppercase tracking-tight">Welcome, {user?.name?.split(" ")[0]}</h1>
        <p className="mt-2 text-zinc-400">Complete your preferences, then find your accountability partner.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard label="Current Partner" value={activePartner ? activePartner.display_name : "—"} hint={activePartner ? "Active" : "Not matched yet"} />
        <StatCard label="Pending Requests" value={(current.pending || []).length} hint="Awaiting response" />
        <StatCard label="Sports" value={sports.length} hint="Selected" />
        <StatCard label="Goals" value={goals.length} hint="Set" />
      </div>

      {activePartner && <MemberAccountability />}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Profile / preferences */}
        <div className="lg:col-span-2 rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6 sm:p-8">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight">Your Preferences</h2>
          <p className="text-sm text-zinc-500 mt-1">We use this to find your ideal accountability partner.</p>

          <div className="mt-6"><PhotoUpload value={photo} onChange={setPhoto} testid="member-photo" /></div>

          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-zinc-300">Country</Label>
              <CountrySelect value={country?.code} onChange={setCountry} testid="member-country-select" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Phone / WhatsApp</Label>
              <PhoneField dialCode={country?.dial} value={phone} onChange={setPhone} testid="member-phone-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">State / Province / Region</Label>
              <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="e.g. California" className="h-11 bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E]" data-testid="member-state-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">City</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Los Angeles" className="h-11 bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E]" data-testid="member-city-input" />
            </div>
          </div>

          <div className="mt-5 space-y-2">
            <Label className="text-zinc-300">About you</Label>
            <Textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell partners about your fitness journey..." className="bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E] min-h-[90px]" data-testid="member-bio-input" />
          </div>

          <div className="mt-6">
            <Label className="text-zinc-300">Sports & interests</Label>
            <div className="mt-3 flex flex-wrap gap-2">
              {SPORTS.map((s) => {
                const Icon = SPORT_ICONS[s.slug];
                const active = sports.includes(s.slug);
                return (
                  <button key={s.slug} onClick={() => toggleSport(s.slug)} data-testid={`member-sport-${s.slug}`}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${active ? "bg-[#E53E3E] border-[#E53E3E] text-white" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
                    <Icon className="w-4 h-4" /> {s.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-zinc-300">Preferred availability</Label>
              <div className="mt-3 flex flex-wrap gap-2">
                {AVAILABILITY_OPTIONS.map((a) => {
                  const active = availability.includes(a);
                  return (
                    <button key={a} onClick={() => toggleAvail(a)} data-testid={`member-avail-${a.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? "bg-[#E53E3E] border-[#E53E3E] text-white" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>{a}</button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Preferred accountability mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-11 bg-zinc-900 border-zinc-800" data-testid="member-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {MEMBER_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-6">
            <Label className="text-zinc-300">Fitness goals</Label>
            <div className="mt-3 flex gap-2">
              <Input value={goal} onChange={(e) => setGoal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addGoal())} placeholder="e.g. Run 5k three times a week" className="h-11 bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E]" data-testid="member-goal-input" />
              <Button type="button" onClick={addGoal} variant="outline" className="border-zinc-700 bg-transparent hover:bg-zinc-800 h-11 rounded-full px-5" data-testid="member-add-goal-button">Add</Button>
            </div>
            {goals.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {goals.map((g) => (
                  <span key={g} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm bg-zinc-800 text-zinc-200">
                    {g}<button onClick={() => setGoals(goals.filter((x) => x !== g))} className="text-zinc-500 hover:text-[#E53E3E]"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <Button onClick={save} disabled={saving} className="mt-7 bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold h-12 px-7" data-testid="member-save-profile-button">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save Profile</>}
          </Button>
        </div>

        {/* Partner column */}
        <div className="space-y-4">
          <MemberBilling />
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6" data-testid="member-partner-panel">
            <h2 className="font-display text-lg font-bold uppercase tracking-tight flex items-center gap-2"><UserCheck className="w-5 h-5 text-[#E53E3E]" /> Your Partner</h2>
            {activePartner ? (
              <div className="mt-4" data-testid="member-active-partner">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full overflow-hidden border border-zinc-700 bg-zinc-800 grid place-items-center shrink-0">
                    {activePartner.photo ? <img src={activePartner.photo} alt={activePartner.display_name} className="w-full h-full object-cover" /> : <User className="w-6 h-6 text-zinc-500" />}
                  </div>
                  <div>
                    <p className="font-display text-lg font-bold uppercase flex items-center gap-1.5">{activePartner.display_name} <BadgeCheck className="w-4 h-4 text-[#E53E3E]" /></p>
                    <p className="text-xs text-zinc-400 flex items-center gap-1"><MapPin className="w-3 h-3" /> {[activePartner.city, activePartner.country].filter(Boolean).join(", ") || "—"}</p>
                  </div>
                </div>
                {activePartner.bio && <p className="mt-3 text-sm text-zinc-400">{activePartner.bio}</p>}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(activePartner.sports || []).map((s) => <span key={s} className="px-2 py-1 rounded-full text-xs bg-zinc-800 text-zinc-300 capitalize">{s.replace("-", " ")}</span>)}
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="mt-4 w-full border-zinc-700 bg-transparent hover:bg-[#E53E3E] hover:text-white rounded-full" data-testid="member-end-match-button">End Relationship</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#0A0A0B] border-zinc-800">
                    <AlertDialogHeader>
                      <AlertDialogTitle>End this accountability relationship?</AlertDialogTitle>
                      <AlertDialogDescription className="text-zinc-400">You'll be able to find and request a new partner afterwards. The history is kept for your records.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-zinc-700 hover:bg-zinc-800">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={endMatch} disabled={ending} className="bg-[#E53E3E] hover:bg-[#C53030]" data-testid="member-end-match-confirm">{ending ? "Ending..." : "End Relationship"}</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ) : (
              <div className="mt-4">
                <p className="text-sm text-zinc-400">You don't have an accountability partner yet.</p>
                <Button onClick={() => setFindOpen(true)} className="mt-4 w-full bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold h-11" data-testid="member-find-partner-button">
                  <Search className="w-4 h-4 mr-2" /> Find a Partner
                </Button>
                {(current.pending || []).length > 0 && (
                  <div className="mt-5" data-testid="member-pending-requests">
                    <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Pending requests</p>
                    <div className="space-y-2">
                      {current.pending.map((req) => (
                        <div key={req.id} className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2">
                          <span className="text-sm text-zinc-300">{req.partner.display_name}</span>
                          <span className="text-xs text-amber-400">Pending</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {!activePartner && <ComingSoon title="Schedule Activities" description="Plan workouts and sessions with your partner." />}
          {!activePartner && <ComingSoon title="Check-ins & Streaks" description="Confirm activities and build your consistency streak." />}
          <ComingSoon title="Rewards & Badges" description="Earn points and unlock perks for staying consistent." />
        </div>
      </div>

      {!activePartner && (
        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          {[{ Icon: CalendarClock, t: "Schedule" }, { Icon: Flame, t: "Streaks" }, { Icon: Award, t: "Rewards" }].map(({ Icon, t }) => (
            <div key={t} className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 opacity-60">
              <Icon className="w-5 h-5 text-[#E53E3E]" /> <span className="text-sm font-medium text-zinc-400">{t} · soon</span>
            </div>
          ))}
        </div>
      )}

      <FindPartnerDialog open={findOpen} onOpenChange={setFindOpen} onRequested={loadMatches} hasActivePartner={!!activePartner} pendingIds={pendingIds} />
    </DashboardShell>
  );
}
